import { useState } from "react";
import {
    Alert,
    AppBar,
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    CssBaseline,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import SportsVolleyballIcon from "@mui/icons-material/SportsVolleyball";
import EventIcon from "@mui/icons-material/Event";
import GroupsIcon from "@mui/icons-material/Groups";
import SportsIcon from "@mui/icons-material/Sports";
import HistoryIcon from "@mui/icons-material/History";
import TableChartIcon from "@mui/icons-material/TableChart";
import StarIcon from "@mui/icons-material/Star";
import DashboardIcon from "@mui/icons-material/Dashboard";
import RuleIcon from "@mui/icons-material/Rule";
import PeopleIcon from "@mui/icons-material/People";
import BoltIcon from "@mui/icons-material/Bolt";
import BadgeIcon from "@mui/icons-material/Badge";
import { useAuth } from "../AuthContext.tsx";
import { useSeason } from "../SeasonContext.tsx";
import RosterDialog from "./Roster/RosterDialog.tsx";
import NavMenu, { NavItem } from "./Home/NavMenu.tsx";
import MatchGallery from "./Home/MatchGallery.tsx";
import NextMatchCard from "./Home/NextMatchCard.tsx";
import SeasonStatsStrip from "./Home/SeasonStatsStrip.tsx";
import TrainingStatsCard from "./Home/TrainingStatsCard.tsx";
import AppFooter from "./Home/AppFooter.tsx";
import ArchiveDialog from "./Home/ArchiveDialog.tsx";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { useIsMobile } from "../hooks/useIsMobile.ts";
import { usePlayerSelection } from "../hooks/usePlayerSelection.ts";
import { useFormationGeneration } from "../hooks/useFormationGeneration.ts";
import { PlayerGrid } from "./PlayerGrid.tsx";
import { PlayerToolbar } from "./PlayerToolbar.tsx";
import FormationResults from "./FormationResults.tsx";
import TrainingTeamsDialog from "./TrainingTeamsDialog.tsx";
import TrainingHistoryDialog from "./TrainingHistoryDialog.tsx";
import BestGroupsDialog from "./BestGroupDialog.tsx";
import DashboardDialog from "./DashboardDialog.tsx";
import CalendarDialog from "./CalendarDialog.tsx";
import RegulationDialog from "./RegulationDialog.tsx";
import ParticipantsDialog from "./ParticipantsDialog.tsx";
import MatchHistoryDialog from "./MatchHistoryDialog.tsx";
import UsageTableDialog from "./Tabellone/UsageTableDialog.tsx";
import QuickMatchDialog from "./QuickMatch/QuickMatchDialog.tsx";

const drawerWidth = 240;
const drawerCollapsedWidth = 72;
const NAV_COLLAPSED_KEY = "navCollapsed";

function readNavCollapsed(): boolean {
    try {
        return localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
    } catch {
        return false;
    }
}

export default function PlayerCards() {
    const {
        players,
        selectedIds,
        setSelectedIds,
        numericFields,
        togglePlayer,
        initials,
        maxValues,
        topPlayers,
        detailPlayer,
        setDetailPlayer,
        openCompareDialog,
        setOpenCompareDialog,
        openCertificateDialog,
        setOpenCertificateDialog,
        newDate,
        setNewDate,
        openCertificateEdit,
        saveCertificate,
        refreshPlayers,
    } = usePlayerSelection();

    const {
        loading,
        results,
        openDialog,
        setOpenDialog,
        generationType,
        setGenerationType,
        topN,
        setTopN,
        openGenerationDialog,
        setOpenGenerationDialog,
        openFormationDialog,
        generateFormations,
        trainingTeams,
        openTrainingDialog,
        setOpenTrainingDialog,
        openSetterDialog,
        setOpenSetterDialog,
        availableSetters,
        selectedSetters,
        setSelectedSetters,
        generateTrainingTeams,
        invokeTrainingService,
    } = useFormationGeneration(selectedIds, players);

    const isMobile = useIsMobile();
    const { auth, logout, isAdmin } = useAuth();
    const { seasons, selectedSeason, isArchived, tournamentId, selectSeason } = useSeason();
    const [rosterOpen, setRosterOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [openGroupsDialog, setOpenGroupsDialog] = useState(false);
    const [dashboardOpen, setDashboardOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [matchDialogOpen, setMatchDialogOpen] = useState(false);
    const [usageOpen, setUsageOpen] = useState(false);
    const [regulationOpen, setRegulationOpen] = useState(false);
    const [participantsOpen, setParticipantsOpen] = useState(false);
    const [quickMatchOpen, setQuickMatchOpen] = useState(false);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [navCollapsed, setNavCollapsed] = useState<boolean>(readNavCollapsed);

    const toggleNavCollapsed = () => {
        setNavCollapsed((c) => {
            try { localStorage.setItem(NAV_COLLAPSED_KEY, c ? "0" : "1"); } catch { /* solo per la sessione */ }
            return !c;
        });
    };
    const navWidth = navCollapsed ? drawerCollapsedWidth : drawerWidth;

    // voci del menù laterale (i dialog sono montati una sola volta più in basso)
    const navItems: NavItem[] = [
        { key: "calendar", label: "Calendario", icon: <EventIcon />, onClick: () => setCalendarOpen(true) },
        { key: "formations", label: "Formazioni", icon: <GroupsIcon />, onClick: openFormationDialog, disabled: isArchived },
        { key: "training", label: "Allenamento", icon: <SportsIcon />, color: "secondary", onClick: generateTrainingTeams, disabled: loading || isArchived },
        { key: "matches", label: "Incontri", icon: <SportsVolleyballIcon />, color: "secondary", onClick: () => setMatchDialogOpen(true) },
        { key: "quick", label: "Partita Veloce", icon: <BoltIcon />, color: "secondary", onClick: () => setQuickMatchOpen(true) },
        { key: "trainings", label: "Allenamenti", icon: <HistoryIcon />, onClick: () => setHistoryDialogOpen(true) },
        { key: "usage", label: "Tabellone", icon: <TableChartIcon />, onClick: () => setUsageOpen(true) },
        { key: "groups", label: "Gruppi", icon: <StarIcon />, onClick: () => setOpenGroupsDialog(true) },
        { key: "dashboard", label: "Dashboard", icon: <DashboardIcon />, onClick: () => setDashboardOpen(true) },
        { key: "archive", label: "Archivio", icon: <PhotoLibraryIcon />, onClick: () => setArchiveOpen(true) },
        { key: "regulation", label: "Regolamento", icon: <RuleIcon />, onClick: () => setRegulationOpen(true) },
        ...(isAdmin ? [{ key: "roster", label: "Rosa", icon: <BadgeIcon />, onClick: () => setRosterOpen(true) }] : []),
        { key: "participants", label: "Partecipanti", icon: <PeopleIcon />, onClick: () => setParticipantsOpen(true) },
    ];

    return (
        <Box sx={{ display: "flex" }}>
            <CssBaseline />

            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: "primary.main" }}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        sx={{ mr: 2, display: { sm: "none" } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box component="img" src={`${process.env.PUBLIC_URL}/vb-logo.png`} alt="VB Volley" sx={{ width: 38, height: 38, mr: 1.2 }} />
                    <Typography variant="h6" noWrap component="div">
                        Vellezzo B. Volley Team Manager
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {selectedSeason && (
                        <Tooltip title="Stagione">
                            <Select
                                size="small"
                                value={selectedSeason.id}
                                onChange={(e) => selectSeason(Number(e.target.value))}
                                sx={{
                                    mr: 2,
                                    color: "white",
                                    fontWeight: 600,
                                    ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.6)" },
                                    ".MuiSvgIcon-root": { color: "white" },
                                }}
                            >
                                {seasons.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.code}{s.current ? " (in corso)" : ""}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Tooltip>
                    )}
                    <Chip
                        label={auth.role}
                        size="small"
                        sx={{ ml: 2, color: "white", borderColor: "rgba(255,255,255,0.7)" }}
                        variant="outlined"
                    />
                    <Tooltip title="Logout">
                        <IconButton color="inherit" onClick={logout} sx={{ ml: 1 }}>
                            <LogoutIcon />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            <Box component="nav" sx={{ width: { sm: navWidth }, flexShrink: { sm: 0 }, transition: "width 0.2s" }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: "block", sm: "none" },
                        "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
                    }}
                >
                    <NavMenu
                        items={navItems.map((i) => ({ ...i, onClick: () => { setMobileOpen(false); i.onClick(); } }))}
                        collapsed={false}
                    />
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: "none", sm: "block" },
                        "& .MuiDrawer-paper": { boxSizing: "border-box", width: navWidth, transition: "width 0.2s", overflowX: "hidden" },
                    }}
                    open
                >
                    <Toolbar />
                    <NavMenu items={navItems} collapsed={navCollapsed} onToggleCollapsed={toggleNavCollapsed} />
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    p: { xs: 2, md: 3 },
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    backgroundImage: `url("/volleyball-vellezzo.jpeg")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    position: "relative",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(255,255,255,0.75)",
                        zIndex: 0,
                    },
                    "& > *": { position: "relative", zIndex: 1 },
                }}
            >
                <Toolbar />
                {isArchived && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Stai consultando la stagione archiviata <strong>{selectedSeason?.code}</strong>:
                        generazione formazioni e allenamenti è disabilitata.
                    </Alert>
                )}
                {!isArchived && players.length === 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        La rosa {selectedSeason?.code} è vuota.
                        {isAdmin ? " Apri \"Rosa\" dal menù per importare i giocatori o aggiungerne di nuovi." : ""}
                    </Alert>
                )}
                <Box
                    sx={{
                        display: "grid",
                        // a sinistra prossimo incontro, giocatori e numeri; a destra la galleria
                        gridTemplateColumns: { xs: "1fr", md: "7fr 5fr", xl: "3fr 2fr" },
                        gap: 3,
                        alignItems: "start",
                    }}
                >
                    <Stack spacing={3} sx={{ minWidth: 0 }}>
                        <NextMatchCard />
                        <Box>
                            <PlayerToolbar
                                players={players}
                                selectedIds={selectedIds}
                                onSelectAll={() => setSelectedIds(players.map((p) => p.id))}
                                onDeselectAll={() => setSelectedIds([])}
                                onCompare={() => setOpenCompareDialog(true)}
                            />
                            <PlayerGrid
                                players={players}
                                selectedIds={selectedIds}
                                topPlayers={topPlayers}
                                loading={loading}
                                onToggle={togglePlayer}
                                onDetail={setDetailPlayer}
                                onCertificateEdit={openCertificateEdit}
                                initials={initials}
                            />
                        </Box>
                        <SeasonStatsStrip />
                        <TrainingStatsCard />
                    </Stack>
                    <Box sx={{ minWidth: 0, position: { md: "sticky" }, top: { md: 80 } }}>
                        <MatchGallery />
                    </Box>
                </Box>

                <Box sx={{ flexGrow: 1 }} />
                <AppFooter />
            </Box>

            {/* DIALOG DEL MENÙ */}
            <CalendarDialog open={calendarOpen} onClose={() => setCalendarOpen(false)} tournamentId={tournamentId} />
            <MatchHistoryDialog open={matchDialogOpen} onClose={() => setMatchDialogOpen(false)} />
            <QuickMatchDialog open={quickMatchOpen} onClose={() => setQuickMatchOpen(false)} />
            <TrainingHistoryDialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} />
            <UsageTableDialog open={usageOpen} onClose={() => setUsageOpen(false)} tournamentId={tournamentId} />
            <BestGroupsDialog open={openGroupsDialog} onClose={() => setOpenGroupsDialog(false)} />
            <DashboardDialog open={dashboardOpen} onClose={() => setDashboardOpen(false)} />
            <RegulationDialog open={regulationOpen} onClose={() => setRegulationOpen(false)} />
            <ArchiveDialog open={archiveOpen} onClose={() => setArchiveOpen(false)} />
            {isAdmin && <RosterDialog open={rosterOpen} onClose={() => setRosterOpen(false)} onChanged={refreshPlayers} />}
            <ParticipantsDialog open={participantsOpen} onClose={() => setParticipantsOpen(false)} />

            {/* DIALOG FORMAZIONI */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile} scroll="paper" PaperProps={{ sx: { borderRadius: 3, maxHeight: "110vh" } }}>
                <DialogTitle sx={{ backgroundColor: "primary.main" }}>
                    Formazioni
                    <IconButton aria-label="close" onClick={() => setOpenDialog(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <FormationResults results={results} players={players.filter((p) => selectedIds.includes(p.id))} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Chiudi</Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG ALLENAMENTO */}
            <Dialog open={openTrainingDialog} onClose={() => setOpenTrainingDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
                <DialogTitle sx={{ backgroundColor: "primary.main" }}>
                    Squadre Allenamento
                    <IconButton aria-label="close" onClick={() => setOpenTrainingDialog(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {trainingTeams && <TrainingTeamsDialog proposal={trainingTeams} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenTrainingDialog(false)}>Chiudi</Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG DETTAGLI GIOCATORE */}
            <Dialog open={!!detailPlayer} onClose={() => setDetailPlayer(null)} maxWidth="md" fullWidth fullScreen={isMobile}>
                <DialogTitle>
                    {detailPlayer?.nickname}
                    <IconButton aria-label="close" onClick={() => setDetailPlayer(null)} sx={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {detailPlayer && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Avatar
                                        sx={{
                                            width: 56, height: 56,
                                            bgcolor: detailPlayer.gender === "M" ? "info.main" : "secondary.light",
                                            color: "white", fontSize: 20,
                                        }}
                                    >
                                        {initials(detailPlayer)}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                            {detailPlayer.firstName} {detailPlayer.lastName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {detailPlayer.roles.join(", ")} • {detailPlayer.age} anni • {detailPlayer.gender}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Skills</Typography>
                                <Grid container spacing={0.5}>
                                    {numericFields.map((field) => (
                                        <Grid size={{ xs: 6 }} key={field}>
                                            <Typography variant="caption" sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <span>{field.charAt(0).toUpperCase() + field.slice(1)}:</span>
                                                <strong>{detailPlayer[field] as unknown as string | number}</strong>
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            {detailPlayer.dynamicStats && (
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Form & Performance</Typography>
                                    <Grid container spacing={2} alignItems="center">
                                        {[
                                            { value: detailPlayer.dynamicStats.form, label: "Training" },
                                            { value: detailPlayer.dynamicStats.matchForm, label: "Match" },
                                        ].map(({ value, label }) => (
                                            <Grid size={4} key={label}>
                                                <Box position="relative" display="inline-flex" sx={{ flexDirection: "column", alignItems: "center" }}>
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={value * 100}
                                                        size={60}
                                                        thickness={4}
                                                        sx={{ color: value >= 0.55 ? "#4caf50" : value >= 0.3 ? "#ff9800" : "#f44336" }}
                                                    />
                                                    <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: "absolute", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{(value * 100).toFixed(0)}%</Typography>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                        <Grid size={4}>
                                            <Box position="relative" display="inline-flex" sx={{ flexDirection: "column", alignItems: "center" }}>
                                                <CircularProgress variant="determinate" value={detailPlayer.dynamicStats.scoreSkill * 100} size={60} thickness={4} sx={{ color: "primary.main" }} />
                                                <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: "absolute", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{detailPlayer.dynamicStats.scoreSkill.toFixed(2)}</Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">Score</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Box mt={2} p={1} bgcolor="grey.100" borderRadius={1}>
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>SETS</Typography>
                                        <Grid container spacing={1} mt={0.5}>
                                            {[
                                                { label: "Total", value: detailPlayer.dynamicStats.sets, color: undefined },
                                                { label: "Won", value: detailPlayer.dynamicStats.won, color: "success.main" },
                                                { label: "Lost", value: detailPlayer.dynamicStats.lost, color: "error.main" },
                                            ].map(({ label, value, color }) => (
                                                <Grid size={4} key={label}>
                                                    <Typography variant="caption" display="block" textAlign="center" sx={color ? { color } : {}}>
                                                        <strong>{value}</strong><br />{label}
                                                    </Typography>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>

                                    <Box mt={1} p={1} bgcolor="grey.100" borderRadius={1}>
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>MATCHES</Typography>
                                        <Grid container spacing={1} mt={0.5}>
                                            {[
                                                { label: "Sets", value: detailPlayer.dynamicStats.matchSetsPlayed, color: undefined },
                                                { label: "Won", value: detailPlayer.dynamicStats.matchSetsWon, color: "success.main" },
                                                { label: "Lost", value: detailPlayer.dynamicStats.matchSetsLost, color: "error.main" },
                                            ].map(({ label, value, color }) => (
                                                <Grid size={4} key={label}>
                                                    <Typography variant="caption" display="block" textAlign="center" sx={color ? { color } : {}}>
                                                        <strong>{value}</strong><br />{label}
                                                    </Typography>
                                                </Grid>
                                            ))}
                                        </Grid>
                                        <Divider sx={{ my: 1 }} />
                                        <Grid container spacing={1}>
                                            <Grid size={6}>
                                                <Typography variant="caption" display="block" textAlign="center">
                                                    <strong>{detailPlayer.dynamicStats.matchesPlayed}</strong> matches played
                                                </Typography>
                                            </Grid>
                                            <Grid size={6}>
                                                <Typography variant="caption" display="block" textAlign="center" sx={{ color: "success.main" }}>
                                                    <strong>{detailPlayer.dynamicStats.matchesWon}</strong> won
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
            </Dialog>

            {/* DIALOG CONFRONTO */}
            <Dialog
                open={openCompareDialog}
                onClose={() => setOpenCompareDialog(false)}
                fullScreen={isMobile}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ backgroundColor: "primary.main" }}>
                    Confronto Giocatori
                    <IconButton aria-label="close" onClick={() => setOpenCompareDialog(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ px: 0, py: 1 }}>
                    <Box sx={{ overflowX: "auto" }}>
                        <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "primary.light" }}>
                                        <TableCell>Av</TableCell>
                                        <TableCell>Nick</TableCell>
                                        <TableCell>Età</TableCell>
                                        <TableCell>G</TableCell>
                                        {numericFields.map((field) => (
                                            <Tooltip key={field} title={field}>
                                                <TableCell>
                                                    {field.charAt(0).toUpperCase()}{field.charAt(field.length - 1)}
                                                </TableCell>
                                            </Tooltip>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {players.filter((p) => selectedIds.includes(p.id)).map((player) => (
                                        <TableRow key={player.id}>
                                            <TableCell>
                                                <Avatar sx={{ width: 24, height: 24, bgcolor: player.gender === "M" ? "info.main" : "secondary.light", color: "white" }}>
                                                    {initials(player)}
                                                </Avatar>
                                            </TableCell>
                                            <TableCell>{player.nickname}</TableCell>
                                            <TableCell>{player.age}</TableCell>
                                            <TableCell>{player.gender}</TableCell>
                                            {numericFields.map((field) => (
                                                <TableCell
                                                    key={field}
                                                    sx={{
                                                        fontWeight: player[field] === maxValues[field] ? "bold" : "normal",
                                                        backgroundColor: player[field] === maxValues[field] ? "success.light" : "transparent",
                                                    }}
                                                >
                                                    {player[field] as unknown as string | number}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCompareDialog(false)}>Chiudi</Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG SCELTA PALLEGGIATORI */}
            <Dialog open={openSetterDialog} onClose={() => setOpenSetterDialog(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
                <DialogTitle sx={{ backgroundColor: "primary.main" }}>
                    Seleziona i due palleggiatori
                    <IconButton aria-label="close" onClick={() => setOpenSetterDialog(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {availableSetters.map((p) => {
                            const checked = selectedSetters.includes(p.id);
                            return (
                                <Card
                                    key={p.id}
                                    sx={{
                                        p: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        border: checked ? "2px solid" : "1px solid",
                                        borderColor: checked ? "primary.main" : "divider",
                                        cursor: "pointer",
                                    }}
                                    onClick={() =>
                                        setSelectedSetters((prev) =>
                                            prev.includes(p.id)
                                                ? prev.filter((id) => id !== p.id)
                                                : prev.length < 2
                                                    ? [...prev, p.id]
                                                    : prev
                                        )
                                    }
                                >
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Avatar sx={{ bgcolor: p.gender === "M" ? "info.main" : "secondary.light", color: "white" }}>
                                            {initials(p)}
                                        </Avatar>
                                        <Typography>{p.nickname}</Typography>
                                    </Stack>
                                    {checked && <CheckIcon color="primary" />}
                                </Card>
                            );
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSetterDialog(false)}>Annulla</Button>
                    <Button
                        variant="contained"
                        disabled={selectedSetters.length !== 2}
                        onClick={() => {
                            setOpenSetterDialog(false);
                            invokeTrainingService(selectedSetters);
                        }}
                    >
                        Conferma
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG CERTIFICATO */}
            <Dialog open={openCertificateDialog} onClose={() => setOpenCertificateDialog(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
                <DialogTitle sx={{ backgroundColor: "primary.main" }}>Aggiorna Certificato</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nuova scadenza :"
                        type="date"
                        sx={{ mt: 2 }}
                        value={newDate}
                        onChange={(e) => setNewDate(e.target.value)}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCertificateDialog(false)}>Annulla</Button>
                    <Button variant="contained" onClick={saveCertificate}>Salva</Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG STRATEGIA GENERAZIONE */}
            <Dialog open={openGenerationDialog} onClose={() => setOpenGenerationDialog(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
                <DialogTitle>Strategia di generazione</DialogTitle>
                <DialogContent dividers>
                    <FormControl component="fieldset" sx={{ width: "100%" }}>
                        <RadioGroup value={generationType} onChange={(e) => setGenerationType(e.target.value as any)}>
                            <FormControlLabel value="all" control={<Radio />} label="Tutte le formazioni" />
                            <FormControlLabel value="topN" control={<Radio />} label="Top N" />
                            <FormControlLabel value="topNEveryone" control={<Radio />} label="Top N (tutti giocano)" />
                        </RadioGroup>
                        {(generationType === "topN" || generationType === "topNEveryone") && (
                            <TextField
                                type="number"
                                label="N"
                                size="small"
                                fullWidth
                                inputProps={{ min: 1 }}
                                value={topN}
                                onChange={(e) => setTopN(Number(e.target.value))}
                                sx={{ mt: 2 }}
                            />
                        )}
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenGenerationDialog(false)}>Annulla</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setOpenGenerationDialog(false);
                            generateFormations();
                        }}
                    >
                        Conferma
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
